import generatingDashboard from '@/assets/lottie/generating_dashboard.json';
import { useAdminContext } from '@/contexts/AdminContext';
import {
    DashboardSchema, Dashboard as DashboardVersion, PatchOperation
} from '@/contexts/AdminContext/dashboardProvider';
import { useAuth } from '@/contexts/AuthContext';
import { Permission } from '@/contexts/AuthContext/permissions';
import { Button } from '@common/index';
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    DragOverEvent,
    DragOverlay,
    DragStartEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors
} from '@dnd-kit/core';
import {
    arrayMove,
    horizontalListSortingStrategy,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MagicWandIcon } from '@phosphor-icons/react';
import clsx from 'clsx';
import Lottie from 'lottie-react';
import {
    Check, ChevronDown, Pin
} from 'lucide-react';
import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import { createPortal } from 'react-dom';
import { replaceCreatedAtFilter } from '../../utils/replaceCreatedAtFilter';
import SubscriptionExpired from '../SubscriptionExpired';
import { DashboardCreationModal } from './DashboardCreationModal';
import { DashboardUI } from './DashboardUI';
import styles from './index.module.scss';

type DashboardWithId = DashboardVersion & { id: string };

const TabItem: React.FC<{
    dashboard: DashboardWithId;
    isActive: boolean;
    isDraft: boolean;
    isPinned?: boolean;
    onSelect?: () => void;
    style?: React.CSSProperties;
    isDragging?: boolean;
    setNodeRef?: (element: HTMLElement | null) => void;
    attributes?: any;
    listeners?: any;
}> = ({
    dashboard,
    isActive,
    isDraft,
    isPinned,
    onSelect,
    style,
    isDragging,
    setNodeRef,
    attributes,
    listeners
}) => {
    return (
        <button
            ref={setNodeRef as any}
            type="button"
            style={style}
            onClick={onSelect}
            className={clsx(
                styles.tabItem,
                isActive && styles.tabItemActive,
                isDragging && styles.dragOverlayItem
            )}
            {...attributes}
            {...listeners}
        >
            <span className={styles.tabName}>{dashboard.title || 'Untitled Dashboard'}</span>
            {isDraft ? <span className={styles.draftBadge}>Draft</span> : null}
            {isPinned ? <Pin size={12} className={styles.pinIcon} /> : null}
        </button>
    );
};

const SortableTabItem: React.FC<{
    dashboard: DashboardWithId;
    isActive: boolean;
    isDraft: boolean;
    isPinned: boolean;
    onSelect: () => void;
}> = ({
    dashboard,
    isActive,
    isDraft,
    isPinned,
    onSelect
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: dashboard.id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1
    };

    if (isDragging) {
        return <div ref={setNodeRef} style={style} className={styles.dragOverlayPlaceholder} />;
    }

    return (
        <TabItem
            dashboard={dashboard}
            isActive={isActive}
            isDraft={isDraft}
            isPinned={isPinned}
            onSelect={onSelect}
            style={style}
            setNodeRef={setNodeRef as any}
            attributes={attributes}
            listeners={listeners}
        />
    );
};

const DashboardTopBar: React.FC<{
    dashboards: DashboardWithId[];
    activeId: string | null;
    isGenerating: boolean;
    pinnedIds: string[];
    onSelect: (id: string) => void;
    onReorder: (newOrder: DashboardWithId[], movedId: string, newIndex: number) => void;
    onCreate: () => void;
    onTogglePin: (dashboardId: string) => void;
    isLoading: boolean;
    canCreateDashboard: boolean;
    syncCompleted: boolean;
}> = ({
    dashboards,
    activeId,
    isGenerating,
    pinnedIds,
    onSelect,
    onReorder,
    onCreate,
    onTogglePin,
    isLoading,
    canCreateDashboard,
    syncCompleted
}) => {
    const [localDashboards, setLocalDashboards] = useState<DashboardWithId[]>(dashboards);
    const isDraggingRef = useRef(false);
    const [activeDragId, setActiveDragId] = useState<string | null>(null);

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownTriggerRef = useRef<HTMLButtonElement>(null);
    const dropdownMenuRef = useRef<HTMLDivElement>(null);
    const [dropdownMenuPosition, setDropdownMenuPosition] = useState<{ top: number; left: number } | null>(
        null
    );
    const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);

    useEffect(() => {
        if (!isDraggingRef.current) {
            setLocalDashboards(dashboards);
        }
    }, [dashboards]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (dropdownTriggerRef.current?.contains(target)) return;
            if (dropdownMenuRef.current?.contains(target)) return;
            setIsDropdownOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (!isDropdownOpen) return;

        const update = () => {
            const rect = dropdownTriggerRef.current?.getBoundingClientRect();
            if (!rect) return;

            const width = 288;
            const margin = 8;
            let left = rect.left;
            if (left + width + margin > window.innerWidth) {
                left = Math.max(margin, window.innerWidth - width - margin);
            }
            const top = rect.bottom + 8;
            setDropdownMenuPosition({ top, left });
        };

        update();
        window.addEventListener('resize', update);
        window.addEventListener('scroll', update, true);
        return () => {
            window.removeEventListener('resize', update);
            window.removeEventListener('scroll', update, true);
        };
    }, [isDropdownOpen]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragStart = (event: DragStartEvent) => {
        isDraggingRef.current = true;
        setActiveDragId(String(event.active.id));
    };

    const handleDragOver = (event: DragOverEvent) => {
        const { active, over } = event;
        if (!over) return;
        if (active.id === over.id) return;

        setLocalDashboards((prev) => {
            const oldIndex = prev.findIndex((d) => d.id === active.id);
            const newIndex = prev.findIndex((d) => d.id === over.id);
            if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return prev;
            const isActivePinned = pinnedSet.has(String(active.id));
            const isOverPinned = pinnedSet.has(String(over.id));
            if (isActivePinned !== isOverPinned) return prev;
            return arrayMove(prev, oldIndex, newIndex);
        });
    };

    const handleDragEnd = (event: DragEndEvent) => {
        isDraggingRef.current = false;
        setActiveDragId(null);

        const { active, over } = event;
        if (!over) return;
        if (active.id === over.id) return;

        const movedId = String(active.id);
        const newIndex = localDashboards.findIndex((d) => d.id === movedId);
        if (newIndex === -1) return;

        onReorder(localDashboards, movedId, newIndex);
    };

    const activeDragDashboard = activeDragId
        ? localDashboards.find((d) => d.id === activeDragId) || null
        : null;

    return (
        <div className={styles.topBar} data-loading={isLoading ? 'true' : 'false'}>
            <div className={styles.tabsContainer}>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={localDashboards.map((d) => d.id)}
                        strategy={horizontalListSortingStrategy}
                    >
                        {localDashboards.map((d) => (
                            <SortableTabItem
                                key={d.id}
                                dashboard={d}
                                isActive={activeId === d.id}
                                isDraft={Boolean(isGenerating && activeId === d.id)}
                                isPinned={pinnedSet.has(d.id)}
                                onSelect={() => onSelect(d.id)}
                            />
                        ))}
                    </SortableContext>

                    <DragOverlay>
                        {activeDragDashboard ? (
                            <TabItem
                                dashboard={activeDragDashboard}
                                isActive={activeId === activeDragDashboard.id}
                                isDraft={Boolean(isGenerating && activeId === activeDragDashboard.id)}
                                isDragging={true}
                                isPinned={pinnedSet.has(activeDragDashboard.id)}
                            />
                        ) : null}
                    </DragOverlay>
                </DndContext>
                {dashboards.length > 3 && (
                <div className={styles.moreWrapper}>
                    <button
                        ref={dropdownTriggerRef}
                        type="button"
                        onClick={() => setIsDropdownOpen((v) => !v)}
                        className={clsx(styles.moreButton, isDropdownOpen && styles.moreButtonOpen)}
                    >
                        All Dashboards
                        <ChevronDown
                            size={14}
                            className={clsx(styles.chevronIcon, isDropdownOpen && styles.chevronOpen)}
                        />
                    </button>

                    {isDropdownOpen && dropdownMenuPosition
                        ? createPortal(
                            <div
                                ref={dropdownMenuRef}
                                style={{
                                    top: dropdownMenuPosition.top,
                                    left: dropdownMenuPosition.left
                                }}
                                className={styles.popoverContent}
                            >
                                <div className={styles.popoverList}>
                                    {dashboards.map((d, idx, arr) => {
                                        const isPinned = pinnedSet.has(d.id);
                                        const next = arr[idx + 1];
                                        const showSeparator = isPinned && next && !pinnedSet.has(next.id);

                                        return (
                                            <React.Fragment key={d.id}>
                                                <div
                                                    onClick={() => {
                                                        setIsDropdownOpen(false);
                                                        onSelect(d.id);
                                                    }}
                                                    className={clsx(
                                                        styles.dashboardRow,
                                                        activeId === d.id && styles.dashboardRowActive
                                                    )}
                                                >
                                                    <span className={styles.dashboardRowName}>
                                                        {d.title || 'Untitled Dashboard'}
                                                    </span>

                                                    <div className={styles.dashboardRowRight}>
                                                        <button
                                                            type="button"
                                                            className={clsx(
                                                                styles.pinButton,
                                                                isPinned
                                                                    ? styles.pinButtonPinned
                                                                    : styles.pinButtonUnpinned
                                                            )}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                onTogglePin(d.id);
                                                            }}
                                                            title={isPinned ? 'Unpin Dashboard' : 'Pin Dashboard'}
                                                        >
                                                            <svg
                                                                width="14"
                                                                height="14"
                                                                viewBox="0 0 24 24"
                                                                fill={isPinned ? 'currentColor' : 'none'}
                                                                stroke="currentColor"
                                                                strokeWidth="2"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            >
                                                                <line x1="12" y1="17" x2="12" y2="22"></line>
                                                                <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"></path>
                                                            </svg>
                                                        </button>

                                                        {activeId === d.id ? (
                                                            <Check size={14} className={styles.activeCheck} />
                                                        ) : null}
                                                    </div>
                                                </div>
                                                {showSeparator ? (
                                                    <div className={styles.pinSeparator}>
                                                     &nbsp;
                                                    </div>
                                                ) : null}
                                            </React.Fragment>
                                        );
                                    })}

                                    {dashboards.length === 0 ? (
                                        <div className={styles.emptyState}>No dashboards found</div>
                                    ) : null}
                                </div>
                            </div>,
                            document.body
                        )
                        : null}
                </div>
                )}
            </div>

            <div className={styles.topBarRight}>
                {canCreateDashboard && syncCompleted ? (
                    <button type="button" onClick={onCreate} className={styles.createButton}>
                        <MagicWandIcon size={20} weight="bold" />
                        Generate
                    </button>
                ) : null}
            </div>
        </div>
    );
};



import { runSelect } from '@/utils/run_select';
import { v4 as uuidv4 } from 'uuid';

export const Dashboard: React.FC = () => {
    const {
        permissions, userBilling, syncStatus 
    } = useAuth();
    const canCreateDashboard = permissions.checkPermissions([Permission.DASHBOARD_MANAGE]);

    const {
        dashboard: {
            isGenerating,
            selectedVersion,
            setSelectedVersion,
            versions,
            isLoadingVersions,
            showCreateModal,
            setShowCreateModal,
            reorderDashboards,
            setCurrentConversationId,
            updateData,
            saveDashboard,
            isEditMode,
            setIsEditMode,
            initEditor,
            dispatchAction
        },
        chat: {setIsChatVisible}
    } = useAdminContext();

    const schema = selectedVersion?.schema;
    const title = selectedVersion?.title || 'Dashboard';
    
    const handleReopen = () => {
        setIsChatVisible(true);
    };

    const handleSelectTemplate = async (template: any) => {
        try {
            let newSchema: DashboardSchema = {};

            if (template.schema) {
                // Deep clone and ensure IDs
                newSchema = JSON.parse(JSON.stringify(template.schema));
            }

            const now = new Date();
            const start = new Date();
            start.setDate(now.getDate() - 29);

            const tempId = uuidv4();
            const tempDashboard: DashboardVersion = {
                id: tempId,
                title: template.title || 'Untitled Dashboard',
                schema: newSchema,
                created_at: now.toISOString(),
                dateRange: {
                    startDate: start,
                    endDate: now
                }
            };

            setSelectedVersion(tempDashboard);

            // Initialize editor with empty schema first
            initEditor(tempId, { kpi: [], chart: [] });

            // Dispatch BATCH_PATCH to populate schema, simulating creation from scratch
            const ops: PatchOperation[] = [{
                op: 'IMPORT',
                path: '',
                value: newSchema
            }];

            if (ops.length > 0) {
                dispatchAction(tempId, {
                    type: 'BATCH_PATCH',
                    payload: ops,
                    description: 'Initialize from template'
                });
            }

            setShowCreateModal(false);
            setIsEditMode(true);


            const promises: any[] = [];

            // Update KPIs data
            if (newSchema.kpi) {
                newSchema.kpi.forEach(kpi => {
                    if (kpi.sql) {
                        const sql = replaceCreatedAtFilter(kpi.sql, start, now);
                        const p = runSelect(sql).then(({ data, error }) => {
                            if (!error && data && Array.isArray(data)) {
                                kpi.data = data;
                            } else {
                                kpi.data = [];
                            }
                        });
                        promises.push(p);
                    }
                });
            }

            // Update Charts data
            if (newSchema.chart) {
                newSchema.chart.forEach(chart => {
                    if (chart.sql) {
                        const sql = replaceCreatedAtFilter(chart.sql, start, now);
                        const p = runSelect(sql).then(({ data, error }) => {
                            if (!error && data && Array.isArray(data)) {
                                chart.data = data;
                            } else {
                                chart.data = [];
                            }
                        });
                        promises.push(p);
                    }
                });
            }

            // Wait for all data to be fetched
            await Promise.all(promises);

            // Update the schema with fetched data
            updateData(tempId, newSchema);

            // Now enter edit mode after data is loaded

        } catch (e) {
            console.error("Failed to create from template", e);
        }
    };

    const EmptyState = ({
        title, description, buttonText, isLoading, onButtonClick
    }:
        {title: string, description: string, buttonText: string, isLoading: boolean, onButtonClick?: () => void}) => {
        return (
            <div className={styles.blankState}>
                <div
                    className={styles.blankStateContent}
                    style={{transform: isLoading ? 'translateY(-80px)' : 'translateY(0px)'}}
                >
                    {isLoading ?(
                        <div
                            className={styles.blankStateIcon}
                            style={{transform: 'translateY(80px)'}}
                        >
                            <Lottie
                                animationData={generatingDashboard}
                                loop={true}
                                style={{ width: '100%', height: 'auto' }}
                            />
                        </div>
                    ) : (
                        <div
                            className={styles.blankStateIcon}
                        >
                            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="48" height="48" rx="12" fill="#1A162F" />
                                <path d="M21.576 11.892C25.4221 11.9129 29.1046 13.4502 31.8242 16.1699C34.5439 18.8895 36.0812 22.572 36.1021 26.4181V26.4252L36.0879 27.0627C35.9473 30.2403 34.7669 33.2909 32.7196 35.7403C32.2907 36.2532 31.5272 36.3216 31.0141 35.8929L20.793 27.3549C20.5175 27.1247 20.3578 26.7842 20.3578 26.4252V13.1031C20.3578 12.7807 20.4864 12.4717 20.715 12.2444C20.9435 12.0172 21.2537 11.8902 21.576 11.892ZM22.78 25.8587L31.5889 33.2164C32.9421 31.2215 33.6774 28.8612 33.6799 26.4323L33.6622 25.8315C33.4986 22.8422 32.2385 20.0093 30.1117 17.8825C28.1318 15.9026 25.5401 14.6739 22.78 14.3816V25.8587Z" fill="white" />
                                <path d="M21.5352 16.2641C22.2038 16.2457 22.761 16.7734 22.7794 17.442C22.7977 18.1105 22.271 18.6677 21.6026 18.6863C20.363 18.7203 19.1511 19.0612 18.0757 19.6786C17.0003 20.296 16.0938 21.1705 15.4394 22.2238C14.7852 23.277 14.4024 24.4765 14.3253 25.714C14.2483 26.9515 14.4797 28.1891 14.9983 29.3154C15.5169 30.4417 16.307 31.422 17.2975 32.1681C18.2879 32.9141 19.4484 33.4021 20.6741 33.5897C21.8996 33.7772 23.1525 33.6587 24.3205 33.2432C25.4888 32.8275 26.5366 32.1274 27.3683 31.2077C27.817 30.712 28.5826 30.674 29.0785 31.1226C29.5745 31.5711 29.6133 32.3367 29.1649 32.8328C28.0644 34.0497 26.6788 34.9746 25.133 35.5246C23.5873 36.0746 21.9293 36.2329 20.3075 35.9847C18.6858 35.7365 17.1508 35.09 15.8404 34.103C14.5298 33.1159 13.4846 31.8181 12.7984 30.3278C12.1123 28.8375 11.8058 27.2 11.9078 25.5626C12.0099 23.9252 12.5169 22.3388 13.3827 20.9453C14.2485 19.5517 15.4465 18.3949 16.8693 17.5781C18.2921 16.7612 19.8952 16.3092 21.5352 16.2641Z" fill="white" />
                            </svg>

                        </div>
                    )}
                    <h2 className={styles.blankStateTitle}>{title}</h2>
                    <p className={styles.blankStateDescription}>
                        {description}
                    </p>
                    <Button
                        onClick={onButtonClick || handleReopen}
                    >
                        {buttonText}
                    </Button>
                </div>
            </div>
        )
    }

    const dashboards = useMemo(() => (
        (versions || [])
            .filter((v): v is DashboardWithId => Boolean(v.id))
    ), [versions]);

    const [pinnedIds, setPinnedIds] = useState<string[]>(() => {
        try {
            const raw = localStorage.getItem('pinned_dashboard_ids');
            if (!raw) return [];
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) return [];
            return parsed.filter((id) => typeof id === 'string');
        } catch {
            return [];
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem('pinned_dashboard_ids', JSON.stringify(pinnedIds));
        } catch {}
    }, [pinnedIds]);

    const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);
    const dashboardsForTopBar = useMemo(() => {
        const pinned: DashboardWithId[] = [];
        const rest: DashboardWithId[] = [];
        dashboards.forEach((d) => {
            if (pinnedSet.has(d.id)) pinned.push(d);
            else rest.push(d);
        });
        return [...pinned, ...rest];
    }, [dashboards, pinnedSet]);

    const activeId = selectedVersion?.id || null;

    const handleSelectDashboard = (id: string) => {
        const target = dashboards.find((d) => d.id === id);
        if (target) setSelectedVersion(target);
    };

    const handleReorderDashboards = (newOrder: DashboardWithId[], movedId: string, newIndex: number) => {
        reorderDashboards(newOrder, movedId, newIndex);
    };

    const handleCreateDashboard = () => {
        setCurrentConversationId(null);
        setShowCreateModal(true);
    };

    const handleTogglePin = (dashboardId: string) => {
        setPinnedIds((prev) => {
            if (prev.includes(dashboardId)) return prev.filter((id) => id !== dashboardId);
            return [dashboardId, ...prev];
        });
    };

    return (
        <div className={styles.layout}>
            <div
                style={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                <DashboardCreationModal
                    isOpen={showCreateModal}
                    onClose={() => setShowCreateModal(false)}
                    onSelectTemplate={handleSelectTemplate}
                />

                {/* 如果订阅已过期，显示 SubscriptionExpired 组件 */}
                {userBilling.isExpired ? (
                    <SubscriptionExpired />
                ) : (
                    <>
                        {!isEditMode && (
                            <DashboardTopBar
                                dashboards={dashboardsForTopBar}
                                activeId={activeId}
                                isGenerating={isGenerating}
                                pinnedIds={pinnedIds}
                                onSelect={handleSelectDashboard}
                                onReorder={handleReorderDashboards}
                                onCreate={handleCreateDashboard}
                                onTogglePin={handleTogglePin}
                                isLoading={isLoadingVersions}
                                canCreateDashboard={canCreateDashboard}
                                syncCompleted={syncStatus.syncCompleted}
                            />
                        )}
                        {schema ? (
                            <DashboardUI
                                key={selectedVersion?.id || selectedVersion?.conversation_id || 'dashboard-ui'}
                                showCreateModal={showCreateModal}
                                setShowCreateModal={setShowCreateModal}
                                schema={schema}
                                createdAt={selectedVersion?.created_at}
                                title={title}
                                selectedVersion={selectedVersion}
                                isGenerating={isGenerating}
                            />
                        ) : (
                            <EmptyState
                                title="Create Your First Dashboard"
                                description="Get started by creating your first dashboard to visualize your data and insights."
                                buttonText="New Dashboard"
                                isLoading={isGenerating}
                                onButtonClick={handleCreateDashboard}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export { DashboardUI };
